/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { Anchor, Button, Modal, Panel, UnorderedList, UnorderedListItem } from '@keplerco/core';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { TertiaryButton } from '../../../../components/general/tertiary-button/tertiary-button';
import { useAppActions, useAppState } from '../../../../overmind';
import { ISkillsStepLayoutProps } from './skills-step.models';
import { ConfirmationModalLayout, ExplainerFocusPanelLayout } from '../../../../widgets/layouts';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { ExitModal } from '../../../../components/modals/exit.modal';
import { SelectionPanel } from '../../../../components/panels/selection/selection.panel';
import { SkillSelectionList } from './skill-selection-list';
import { IncludeEntityCard } from '../include-entity-card';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { PeerAssessmentAssigneeResponse, PeerAssessmentRequest, PeerAssessmentResponse } from '../../../../overmind/actions';
import { SkillsAccordion } from '../rating-assessment-accordion/skills-accordion';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { RatingAssessmentEmptyStateImage } from '../rating-assessment-empty-state.image';
import { PagePath } from '../../../../navigation/navigation.enums';

export function SkillsStepLayout({ isLoading, setIsLoading, assessmentSlug, onBack, onNext, ...props }: ISkillsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [peerAssessment, setPeerAssessment] = useState<PeerAssessmentResponse>(props.peerAssessment);
  const [isExplainerPanelOpen, setIsExplainerPanelOpen] = useState(false);
  const [isIncludingRoleSkills, setIsIncludingRoleSkills] = useState(false);

  const [isSelectionPanelOpen, setIsSelectionPanelOpen] = React.useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<PeerAssessmentAssigneeResponse>();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSaveDraft, setIsSaveDraft] = useState<boolean | null>(null);

  useEffect(() => {
    if (isSaveDraft === null) return; 
  
    if (isSaveDraft) {
      actions.dismissAllNotifications();
      actions.addNotification({
        id: crypto.randomUUID(),
        active: true,
        priority: NotificationPriority.Toast,
        toastIncludeIcon: true,
        type: NotificationType.Warn,
        title: 'This assessment is now a draft',
        message: 'You will need to schedule this once your changes are complete',
      });
      keplerNavigate(
        `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`
      );
    } else {
      onNext(peerAssessment);
    }
  
    setIsSaveDraft(null);
    setIsLoading(false);
  }, [isSaveDraft]);
  

  useEffect(() => {
    if (hasUnsavedChanges) return;
    setHasUnsavedChanges(JSON.stringify(peerAssessment.assignees) !== JSON.stringify(props.peerAssessment.assignees));
  }, [peerAssessment]);

  async function includeRoleSkills() {
    setIsIncludingRoleSkills(true);

    const response = await actions.getPeerAssessmentAssigneesRoleSkills(assessmentSlug);
    setPeerAssessment(currentState => {
      const nextState: PeerAssessmentResponse = structuredClone(currentState);
      response?.forEach(assignee => {
        const assigneeIndex = nextState.assignees.findIndex(a => a.learnerSlug === assignee.learnerSlug);
        if (assigneeIndex === -1) return;

        assignee.roleSkills.forEach(roleSkill => {
          const companySkillIndex = nextState.assignees[assigneeIndex].companySkills.findIndex(companySkill => companySkill.slug === roleSkill.slug);
          if (companySkillIndex !== -1) return;
          nextState.assignees[assigneeIndex].companySkills.push(roleSkill);
        });
      });
      return nextState;
    });

    actions.dismissAllNotifications();
    actions.addNotification({
      active: true,
      id: crypto.randomUUID(),
      type: NotificationType.Success,
      title: 'Role skills added',
      message: 'Role skills have been added to each subject',
      priority: NotificationPriority.Toast,
      toastIncludeIcon: true,
    });

    setIsIncludingRoleSkills(false);
  }

  function removeAllSkills() {
    setPeerAssessment(currentState => {
      const nextState: PeerAssessmentResponse = structuredClone(currentState);
      nextState.assignees.forEach(assignee => (assignee.companySkills = []));
      return nextState;
    });
    setIsConfirmationModalOpen(false);
  }

  async function saveSkills(isSaveDraft?: boolean) {
    setIsLoading(true);

    const request: PeerAssessmentRequest = {
      ...peerAssessment,
      assignees: peerAssessment.assignees.map(assignee => ({
        ...assignee,
        reviewers: assignee.reviewers.map(reviewer => ({ learnerSlug: reviewer.learnerSlug, isManager: reviewer.isManager })),
        companySkillIds: assignee.companySkills.map(companySkill => companySkill.id),
      })),
    };
    await actions.updatePeerAssessment({ assessmentSlug, peerAssessment: request });

    setHasUnsavedChanges(false);
    setIsSaveDraft(isSaveDraft ?? false);
  }

  return (
    <>
      <div className={assessmentsWizardStyles.stepContainer}>
        <SkeletonOverlay isLoading={isLoading}>
          <WidgetCard onHelpClick={() => setIsExplainerPanelOpen(true)} title="Skills" subtitle="Select the skills that you want to assess for each subject">
            <div className={assessmentsWizardStyles.stepActionCards}>
              <IncludeEntityCard isLoading={isIncludingRoleSkills} title="Include role skills" subtitle="Add each subject's role skills to the assessment" buttonText={isIncludingRoleSkills ? 'Including...' : 'Include'} action={includeRoleSkills} />
              <IncludeEntityCard title="Select skills for all subjects" subtitle="Add a selection of skills to all subjects" buttonText="Select" action={() => setIsSelectionPanelOpen(true)} />
            </div>

            {!!peerAssessment.assignees.length ? (
              <React.Fragment>
                <div className={assessmentsWizardStyles.stepBodyHeading}>
                  <h4>Add or edit the skills for each subject</h4>

                  <TertiaryButton type="button" style={{ marginLeft: 'auto' }} disabled={!peerAssessment.assignees.some(assignee => !!assignee.companySkills.length)} onClick={() => setIsConfirmationModalOpen(true)}>
                    Remove all skills
                  </TertiaryButton>
                </div>

                {peerAssessment.assignees.map(assignee => (
                  <SkillsAccordion
                    key={assignee.learnerSlug}
                    assignee={assignee}
                    onAdd={() => setSelectedAssignee(assignee)}
                    onRemove={slug =>
                      setPeerAssessment(currentState => {
                        const nextState: PeerAssessmentResponse = structuredClone(currentState);
                        const index = nextState.assignees.findIndex(a => a.learnerSlug === assignee.learnerSlug);
                        nextState.assignees[index].companySkills = nextState.assignees[index].companySkills.filter(companySkill => companySkill.slug !== slug);
                        return nextState;
                      })
                    }
                  />
                ))}
              </React.Fragment>
            ) : (
              <EmptyState title="" subtitle="The selected subjects will display here with their skills" imageElement={<RatingAssessmentEmptyStateImage />} />
            )}
          </WidgetCard>
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor reverse arrow onClick={onBack}>
            Back
          </Anchor>

          <div className={assessmentsWizardStyles.stepFooterButtonGroup}>
            <Anchor aria-label="Save as draft and exit" onClick={() => saveSkills(true)}>
              Save draft
            </Anchor>

            <Button disabled={isLoading} filled type="button" arrow aria-label="Save and continue to next step" onClick={() => saveSkills()}>
              Next
            </Button>
          </div>
        </footer>
      </div>

      <Panel open={!!isExplainerPanelOpen} onClose={() => setIsExplainerPanelOpen(false)}>
        <ExplainerFocusPanelLayout title="Understanding assessment skills" subtitle="Choose the right skills for each subject with full flexibility and control" onClose={() => setIsExplainerPanelOpen(false)}>
          <React.Fragment>
            <p>When setting up your assessment, you have three ways to add skills. You can use any combination of these to match your assessment needs.</p>
            <br />
            <h3>How it works</h3>
            <br />
            <p className="bold" style={{ marginBottom: 8 }}>
              You can assign skills in three ways:
            </p>
            <UnorderedList gap="8px">
              <UnorderedListItem>Include role skills - Automatically add each subject's role-based skills.</UnorderedListItem>
              <UnorderedListItem>Select skills for all subjects - Apply a shared set of skills to every subject in the assessment.</UnorderedListItem>
              <UnorderedListItem>Add or edit skills per subject - Choose individual skills for each person from your organisation's skill library.</UnorderedListItem>
            </UnorderedList>
            <br />
            <p>Each method adds skills to the assessment. You can mix and match them to create the right configuration.</p>
            <br />
            <p>You can also remove specific skills from any subject. This is useful if a role includes too many skills or if some aren't relevant to the current assessment.</p>
            <br />
            <p className="bold">Need to start over?</p>
            <br />
            <p>You can choose to remove all skills, but be aware this will clear everything you've added so far. A confirmation step will appear to help prevent accidental resets.</p>
          </React.Fragment>
        </ExplainerFocusPanelLayout>
      </Panel>

      <Panel
        open={isSelectionPanelOpen || !!selectedAssignee}
        onClose={() => {
          setIsSelectionPanelOpen(false);
          setSelectedAssignee(undefined);
        }}
        size="fullscreen"
      >
        {(isSelectionPanelOpen || !!selectedAssignee) && (
          <SelectionPanel
            title="Skills"
            subtitle="Select the skills you'd like to include in the assessment"
            selectedEntities={!!selectedAssignee ? selectedAssignee.companySkills.map(companySkill => ({ slug: companySkill.slug, name: companySkill.name, entity: companySkill })) : []}
            entityType="skills"
            onCancel={() => {
              setIsSelectionPanelOpen(false);
              setSelectedAssignee(undefined);
            }}
            onAdd={selectedEntities => {
              const companySkills = selectedEntities.map(entity => entity.entity);
              setPeerAssessment(currentState => {
                const nextState: PeerAssessmentResponse = structuredClone(currentState);
                if (!!selectedAssignee) {
                  const index = nextState.assignees.findIndex(assignee => assignee.learnerSlug === selectedAssignee?.learnerSlug);
                  nextState.assignees[index].companySkills = companySkills;
                } else {
                  nextState.assignees.forEach(assignee => {
                    assignee.companySkills.push(...companySkills.filter(companySkill => !assignee.companySkills.some(assigneeSkill => assigneeSkill.slug === companySkill.slug)));
                  });
                }
                return nextState;
              });
              setSelectedAssignee(undefined);
              setIsSelectionPanelOpen(false);
            }}
          >
            {({ selectedEntities, setSelectedEntities }) => <SkillSelectionList selectedEntities={selectedEntities} setSelectedEntities={setSelectedEntities} />}
          </SelectionPanel>
        )}
      </Panel>

      <Modal open={isConfirmationModalOpen} onClose={() => setIsConfirmationModalOpen(false)}>
        <ConfirmationModalLayout title="Are you sure you want to remove all skills from this assessment?" submitButtonText="Yes, remove" cancelButtonText="No, cancel" onClickSubmit={removeAllSkills} onClickCancel={() => setIsConfirmationModalOpen(false)} />
      </Modal>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </>
  );
}
